import { useState, type FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useGetEnrollmentHYCSVerifyPhoneData, useSubmitEnrollmentHYCSVerifyPhoneOTPData } from '@egw/api'
import { Alert, AlertType, GenericError, Input } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { EnrollmentHYCSLayout } from '@egw/layouts'
import { sendEvent } from '@egw/events'
import { CheckIcon } from '@heroicons/react/24/outline'
import { Controller, useForm } from 'react-hook-form'
import { useRouter } from '@egw/router'
import { WEALTH_ROUTES } from '@egw/constants'
import { useValidateRoute } from '../../hooks/navigation/validate-route'
import { EDataLayerEventWealth } from '../../constants/events/EDataLayerEventWealth'
import { formatPhoneNumber } from '../../utils/phone-number/phone-number'
import { PageHeader } from '../page-header/page-header'
import { getTestId, HtmlTestTypes } from '../../utils/testing/get-test-id'
import { newPathIsDifferent } from '../../utils/general/new-path-is-different'
import { ROUTES } from '../../constants/routes/routes-generic'

const log = new Logger('Enrollment - HYCS - VerifyPhone')

export const VerifyPhoneOtp: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const { navigate } = useRouter()

  const { data, isLoading, error } = useGetEnrollmentHYCSVerifyPhoneData()
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentHYCSVerifyPhoneOTPData()

  const [codeResent, setCodeResent] = useState(false)
  const [counter, setCounter] = useState(0)

  const phoneNumber = data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData.phoneNumber : ''
  const applicationId = data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData.applicationId : ''

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors: formErrors, isValid },
  } = useForm({
    defaultValues: {
      code: '',
    },
  })

  // Protect route
  useValidateRoute(data?.path)

  const resendOtp = async (): Promise<void> => {
    setCodeResent(true)

    try {
      const response = await mutate({
        triggerPhoneOtp: true,
      })

      sendEvent(EDataLayerEventWealth.BANKHYCHYS_PHONEOTPRESEND_SUBMIT, {
        applicationId,
      })

      if (response?.__typename === 'SubmitEnrollmentSuccess') {
        sendEvent(EDataLayerEventWealth.BANKHYCHYS_PHONE_VERIFIED)
      }

      setTimeout(() => {
        setCounter(30)
        const intervalId = setInterval(() => {
          setCounter((prev) => {
            if (prev <= 1) {
              clearInterval(intervalId)
              setCodeResent(false)

              return 0
            }
            return prev - 1
          })
        }, 1000)
      }, 5000)
    } catch (e) {
      log.error('Error submitting form', e)
    }
  }

  const onSubmit = handleSubmit(async (fData: { code: string }): Promise<void> => {
    const formData = fData
    if (isValid) {
      try {
        sendEvent(EDataLayerEventWealth.BANKHYCHYS_ENROLLMENT_STARTED)
        const response = await mutate({
          phoneOtpCode: formData.code,
        })
        // If path exists and is different
        if (response?.path && newPathIsDifferent(location.pathname, response.path)) {
          navigate(response.path)
        } else {
          // Stay on page if path is same
          if (['INVALID_TOKEN', 'USED_OR_EXPIRED_OTP_EXCEPTION'].includes(response?.type || '')) {
            setError('code', {
              type: 'manual',
              message: t('common.verify_phone.otp.code_expired'),
            })
          } else {
            // If no path from BE, redirect to error
            if (!response?.path) {
              navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ROUTES.ERROR.path}`)
            }
          }
        }
      } catch (e) {
        log.error('Error submitting', e)
      }
    }
  })

  return (
    <EnrollmentHYCSLayout title={t('common.verify_phone.otp.page_title')}>
      <div className="egw-container-enroll-hycs h-full flex flex-col relative md:justify-center justify-start items-start">
        {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
        {isLoading ? (
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        ) : null}

        {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
        {!isLoading && !error ? (
          <form
            onSubmit={(e) => void onSubmit(e)}
            className="w-full flex-1 flex flex-col justify-start lg:justify-center"
          >
            <PageHeader title={t('common.verify_phone.otp.title')} />
            <p className="typo-primary-base-regular text-text-meta mb-3xs">{t('common.verify_phone.otp.sent_to')}</p>
            <p className="typo-primary-base-semibold text-text-header mb-md fs-exclude">
              {formatPhoneNumber(phoneNumber)}
            </p>

            <Controller
              name="code"
              control={control}
              rules={{
                required: t('common.required_field'),
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  label={t('common.verify_phone.otp.code_label')}
                  required
                  error={formErrors.code?.message}
                  type="input"
                  className="mb-sm"
                  maxLength={6}
                  onBlur={onSubmit}
                  testId={getTestId('code', HtmlTestTypes.input)}
                />
              )}
            />

            <div className="flex items-baseline gap-4">
              <button
                onClick={() => void resendOtp()}
                disabled={codeResent || isSubmitting}
                className={`typo-primary-sm-semibold text-left w-fit ${codeResent ? 'text-gray-gray-600' : ' text-text-action'}`}
                type="button"
              >
                {t('common.verify_phone.otp.resend_message')}
              </button>
              {codeResent && !counter ? (
                <p className="typo-primary-sm-semibold text-text-header-success flex items-center gap-2">
                  {t('common.verify_phone.otp.message_sent')} <CheckIcon className="size-5" />
                </p>
              ) : null}
            </div>

            {counter ? (
              <p className="typo-primary-sm-semibold text-text-placeholder mt-2xs">
                {t('common.verify_phone.otp.send_another_message', {
                  time: counter,
                })}
              </p>
            ) : null}

            <div className="flex-1 md:hidden" />

            {submitError ? <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" /> : null}

            {/* AUTO GENERATED COMPONENTS */}
          </form>
        ) : null}

        {/* This error handles the case when the initial data is not loaded, for example network error */}
        {error ? (
          <div className="flex-1 flex flex-col justify-start md:justify-center">
            <GenericError errorMessage={t('common.generic_error')} />
          </div>
        ) : null}
      </div>
    </EnrollmentHYCSLayout>
  )
}
